import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const InvoiceTypeSchema = z.enum(['PHONE_PLAN', 'EQUIPMENT']);
export const InvoiceKindSchema = z.enum(['INVOICE', 'DETAIL']);
const InvoiceSiteSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Vendor id still exposed here — tracked for removal (chantier 7). */
    externalSiteId: z.string(),
});
const InvoiceBaseSchema = z.object({
    id: z.string(),
    reference: z.string(),
    type: InvoiceTypeSchema.nullable(),
    kind: InvoiceKindSchema,
    date: z.string(),
    publicId: z.string(),
    linkedInvoiceId: z.string().nullable(),
    sites: z.array(InvoiceSiteSchema),
});
export const InvoiceSchema = InvoiceBaseSchema.extend({
    linkedDetails: z.array(InvoiceBaseSchema).optional(),
});
export const InvoicesResponseSchema = z.object({
    phonePlanInvoices: z.record(z.array(InvoiceSchema)),
    equipmentInvoices: z.array(InvoiceSchema),
});
export const UpdateInvoiceBodySchema = z.object({
    reference: z.string().optional(),
    type: InvoiceTypeSchema.optional(),
    date: z.string().optional(),
    siteIds: z.array(z.string()).optional(),
});
export const LinkDetailBodySchema = z.object({
    /** Invoice to link the detail to, or null to unlink. */
    invoiceId: z.string().nullable(),
});
const invoiceParams = z.object({
    organizationId: z.string().min(1),
    invoiceId: z.string().min(1),
});
/**
 * Multipart fields of the invoice upload — everything is a string on the
 * wire, so enums/date/siteIds are coerced here. The `file` entry of the
 * FormData is handled by the server interceptor, not the schema.
 */
export const UploadInvoiceBodySchema = z.object({
    tenantId: z.string().min(1),
    reference: z.string().min(1),
    type: z.preprocess((value) => (value ? value : null), InvoiceTypeSchema.nullable()),
    kind: z.preprocess((value) => (value ? value : undefined), InvoiceKindSchema.optional()),
    date: z.coerce.date(),
    siteIds: z
        .string()
        .optional()
        .transform((value, ctx) => {
        if (!value)
            return [];
        let parsed;
        try {
            parsed = JSON.parse(value);
        }
        catch {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'siteIds must be a JSON array of strings',
            });
            return z.NEVER;
        }
        const result = z.array(z.string()).safeParse(parsed);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'siteIds must be a JSON array of strings',
            });
            return z.NEVER;
        }
        return result.data;
    }),
});
/** Invoice reads, mutations and the multipart upload. */
export const invoiceContract = c.router({
    uploadInvoice: {
        method: 'POST',
        path: '/organization/:organizationId/invoices/upload',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        contentType: 'multipart/form-data',
        body: UploadInvoiceBodySchema,
        responses: {
            201: z.object({ id: z.string(), publicId: z.string() }),
        },
        summary: 'Upload an invoice file (PDF/Excel) with its metadata',
    },
    listInvoices: {
        method: 'GET',
        path: '/organization/:organizationId/invoices',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: InvoicesResponseSchema },
        summary: 'Invoices of an organization, grouped for the Factures tab',
    },
    getInvoiceDownloadUrl: {
        method: 'GET',
        path: '/organization/:organizationId/invoices/:invoiceId/download',
        pathParams: invoiceParams,
        responses: {
            200: z.object({ downloadUrl: z.string(), expiresIn: z.number() }),
        },
        summary: 'Signed download URL of an invoice file',
    },
    updateInvoice: {
        method: 'PATCH',
        path: '/organization/:organizationId/invoices/:invoiceId',
        pathParams: invoiceParams,
        body: UpdateInvoiceBodySchema,
        responses: { 200: z.object({ message: z.string() }) },
        summary: 'Update the metadata of an invoice',
    },
    linkDetail: {
        method: 'PATCH',
        path: '/organization/:organizationId/invoices/:detailId/link',
        pathParams: z.object({
            organizationId: z.string().min(1),
            detailId: z.string().min(1),
        }),
        body: LinkDetailBodySchema,
        responses: { 200: z.object({ message: z.string() }) },
        summary: 'Attach a billing detail to an invoice, or detach it',
    },
    deleteInvoice: {
        method: 'DELETE',
        path: '/organization/:organizationId/invoices/:invoiceId',
        pathParams: invoiceParams,
        body: c.noBody(),
        responses: { 200: z.object({ message: z.string() }) },
        summary: 'Delete an invoice',
    },
});
